import { useState } from 'react'

export const ConvictionScoring = () => {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<any>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [formData, setFormData] = useState({
    symbol: 'NVDA',
    catalystType: 'earnings',
    timeHorizon: '3m',
    marketConditions: 'normal'
  })

  const handleAnalyze = async () => {
    setLoading(true)
    try {
      // Simulate API call with dynamic results
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      setResults({
        asset: { symbol: formData.symbol, name: `${formData.symbol} Conviction Analysis` },
        conviction: {
          overall: Math.floor(Math.random() * 30) + 70,
          fundamental: Math.floor(Math.random() * 40) + 60,
          technical: Math.floor(Math.random() * 40) + 60,
          sentiment: Math.floor(Math.random() * 40) + 60,
          momentum: Math.floor(Math.random() * 40) + 60
        },
        catalysts: [
          { 
            type: 'Earnings Beat', 
            probability: Math.floor(Math.random() * 30) + 65, 
            impact: 'High',
            timeframe: '2 weeks'
          },
          { 
            type: 'Product Launch', 
            probability: Math.floor(Math.random() * 25) + 70, 
            impact: 'Medium',
            timeframe: '1 month'
          },
          { 
            type: 'Partnership Deal', 
            probability: Math.floor(Math.random() * 20) + 55, 
            impact: 'High',
            timeframe: '3 months'
          }
        ],
        recommendation: Math.random() > 0.6 ? 'high conviction' : Math.random() > 0.3 ? 'medium conviction' : 'low conviction',
        confidence: Math.floor(Math.random() * 15) + 82
      })
    } catch (error) {
      console.error('Analysis failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Conviction & Catalyst Scoring Engine</h1>
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="w-6 h-6 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center text-blue-600 text-sm font-medium transition-colors"
            >
              ?
            </button>
            {showTooltip && (
              <div className="absolute z-10 w-80 p-3 mt-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg">
                <div className="font-medium mb-1">What does this module do?</div>
                <div className="text-gray-300">
                  Measures investment conviction levels by analyzing potential catalysts (earnings, product launches, partnerships) 
                  and their probability of occurrence. Combines fundamental strength, technical momentum, and sentiment analysis 
                  to determine high/medium/low conviction recommendations with catalyst timing predictions.
                </div>
                <div className="absolute top-0 left-6 -mt-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              </div>
            )}
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          Investment confidence metrics and catalyst analysis
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Analysis Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset Symbol
            </label>
            <input
              type="text"
              value={formData.symbol}
              onChange={(e) => setFormData({...formData, symbol: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catalyst Type
            </label>
            <select 
              value={formData.catalystType}
              onChange={(e) => setFormData({...formData, catalystType: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="earnings">Earnings</option>
              <option value="product">Product Launch</option>
              <option value="partnership">Partnership</option>
              <option value="regulatory">Regulatory</option>
              <option value="market">Market Event</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Horizon
            </label>
            <select 
              value={formData.timeHorizon}
              onChange={(e) => setFormData({...formData, timeHorizon: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="1m">1 Month</option>
              <option value="3m">3 Months</option>
              <option value="6m">6 Months</option>
              <option value="1y">1 Year</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Conditions
            </label>
            <select 
              value={formData.marketConditions}
              onChange={(e) => setFormData({...formData, marketConditions: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="bull">Bull Market</option>
              <option value="normal">Normal</option>
              <option value="bear">Bear Market</option>
              <option value="volatile">High Volatility</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <span>🔍</span>
            {loading ? 'Analyzing...' : 'Run Conviction Analysis'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Conviction Scores */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Conviction Scores</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(results.conviction).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{value as number}</div>
                  <div className="text-sm text-gray-600 capitalize">{key}</div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-600">Recommendation: </span>
                  <span className={`font-semibold capitalize ${
                    results.recommendation.includes('high') ? 'text-green-600' : 
                    results.recommendation.includes('medium') ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {results.recommendation}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Confidence: </span>
                  <span className="font-semibold">{results.confidence}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Catalyst Analysis */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Catalyst Analysis</h2>
            
            <div className="space-y-4">
              {results.catalysts.map((catalyst: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{catalyst.type}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      catalyst.impact === 'High' ? 'bg-red-100 text-red-800' :
                      catalyst.impact === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {catalyst.impact} Impact
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Probability: </span>
                      <span className="font-medium">{catalyst.probability}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Timeframe: </span>
                      <span className="font-medium">{catalyst.timeframe}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}